import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ActionBarPrimitive,
  AssistantRuntimeProvider,
  AuiIf,
  ComposerPrimitive,
  MessagePrimitive,
  SuggestionPrimitive,
  Suggestions,
  ThreadPrimitive,
  useAuiState,
  useAui,
  useLocalRuntime,
} from "@assistant-ui/react";
import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import { ArrowDownIcon, ArrowUpIcon, CopyIcon, RefreshCwIcon, Share2Icon, SquareIcon } from "lucide-react";
import remarkGfm from "remark-gfm";

const RECORD_SUGGESTIONS = [
  {
    title: "Summarize this",
    label: "",
    prompt: "Summarize this record and name the most important accountability questions.",
  },
  {
    title: "Find oversight questions",
    label: "",
    prompt: "Find provisions, transactions, or claims in this record that deserve oversight.",
  },
  {
    title: "What should citizens verify?",
    label: "",
    prompt: "What public records should citizens verify before trusting this record's claims?",
  },
  {
    title: "Show possible conflicts",
    label: "",
    prompt: "Show possible conflicts or influence concerns visible in this record.",
  },
];

function RecordAssistant({ source, recordId }) {
  const modelAdapter = useMemo(
    () => ({
      async run({ messages, abortSignal }) {
        const userMessage = [...messages].reverse().find((message) => message.role === "user");
        const question = textFromContent(userMessage?.content);
        const selectedText = document.getSelection()?.toString() || "";
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            question,
            messages: messages.map((message) => ({
              role: message.role,
              content: message.content,
            })),
            source,
            recordId,
            selectedText,
          }),
          signal: abortSignal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || `Request failed with status ${response.status}`);
        }
        return {
          content: [{ type: "text", text: payload.answer || "" }],
        };
      },
    }),
    [recordId, source],
  );
  const runtime = useLocalRuntime(modelAdapter);
  const aui = useAui({
    suggestions: Suggestions(RECORD_SUGGESTIONS),
  });

  return (
    <AssistantRuntimeProvider aui={aui} runtime={runtime}>
      <RecordThread source={source} recordId={recordId} />
    </AssistantRuntimeProvider>
  );
}

function RecordThread({ source, recordId }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const updateHeight = () => {
      const root = rootRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const documentHeight = document.querySelector("[data-record-document]")?.getBoundingClientRect().height || Infinity;
      const viewportSpace = window.innerHeight - rect.top - 16;
      const targetHeight = Math.min(Math.max(viewportSpace, 520), documentHeight, 880);
      root.style.setProperty("--t47-assistant-height", `${Math.round(targetHeight)}px`);
    };
    updateHeight();
    const documentNode = document.querySelector("[data-record-document]");
    const observer = typeof ResizeObserver === "function" && documentNode ? new ResizeObserver(updateHeight) : null;
    const mutationObserver =
      typeof MutationObserver === "function" && documentNode ? new MutationObserver(updateHeight) : null;
    const timers = [100, 500, 1500].map((delay) => window.setTimeout(updateHeight, delay));
    observer?.observe(documentNode);
    mutationObserver?.observe(documentNode, { childList: true, subtree: true });
    window.addEventListener("resize", updateHeight);
    window.addEventListener("scroll", updateHeight, { passive: true });
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      observer?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", updateHeight);
    };
  }, []);

  return (
    <ThreadPrimitive.Root className="t47-assistant-thread" ref={rootRef}>
      <div className="t47-assistant-thread__header">
        <div className="t47-assistant-thread__header-row">
          <h2>Ask AI</h2>
          <ShareTranscriptButton source={source} recordId={recordId} />
        </div>
        <p>Ask about this record. Highlight text in the document to include that passage.</p>
      </div>
      <ThreadPrimitive.Viewport className="t47-assistant-thread__viewport" turnAnchor="top">
        <AuiIf condition={(state) => state.thread.isEmpty}>
          <ThreadWelcome />
        </AuiIf>
        <div className="t47-assistant-thread__messages">
          <ThreadPrimitive.Messages>{() => <ThreadMessage />}</ThreadPrimitive.Messages>
        </div>
        <ThreadPrimitive.ViewportFooter className="t47-assistant-thread__footer">
          <ThreadScrollToBottom />
          <Composer />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
}

function ShareTranscriptButton({ source, recordId }) {
  const messages = useAuiState((state) => state.thread.messages);
  const isRunning = useAuiState((state) => state.thread.isRunning);
  const [status, setStatus] = useState("idle");
  const [shareUrl, setShareUrl] = useState("");
  const [error, setError] = useState("");
  const hasMessages = messages.some((message) => textFromContent(message.content).trim());

  const publish = async () => {
    if (!hasMessages || status === "publishing") return;
    const confirmed = window.confirm(
      "This will create a permanent public-by-link transcript on share.voxvey.com. Anyone with the link can view it. Continue?",
    );
    if (!confirmed) return;
    setStatus("publishing");
    setError("");
    setShareUrl("");
    try {
      const body = buildShareRequest({ source, recordId, messages });
      const response = await fetch("/api/share-transcript", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `Request failed with status ${response.status}`);
      }
      setShareUrl(payload.url || "");
      setStatus("published");
    } catch (shareError) {
      setError(shareError.message || "Transcript publish failed.");
      setStatus("idle");
    }
  };

  return (
    <div className="t47-assistant-share">
      <button
        className="t47-assistant-share__button"
        disabled={!hasMessages || isRunning || status === "publishing"}
        onClick={publish}
        title="Share transcript to Voxvey"
        type="button"
      >
        <Share2Icon aria-hidden="true" size={14} />
        <span>{status === "publishing" ? "Sharing" : "Share"}</span>
      </button>
      {shareUrl ? (
        <a className="t47-assistant-share__link" href={shareUrl} rel="noreferrer" target="_blank">
          Public link
        </a>
      ) : null}
      {error ? <span className="t47-assistant-share__error">{error}</span> : null}
    </div>
  );
}

function ThreadWelcome() {
  return (
    <div className="t47-assistant-welcome">
      <div className="t47-assistant-welcome__copy">
        <h3>Start with the record.</h3>
        <p>Use a suggested prompt or ask a narrower question in the composer.</p>
      </div>
      <ThreadSuggestions />
    </div>
  );
}

function ThreadSuggestions() {
  return (
    <div className="t47-assistant-suggestions" aria-label="Suggested record questions">
      <ThreadPrimitive.Suggestions>{() => <ThreadSuggestionItem />}</ThreadPrimitive.Suggestions>
    </div>
  );
}

function ThreadSuggestionItem() {
  return (
    <SuggestionPrimitive.Trigger send className="t47-assistant-suggestion" type="button">
      <span className="t47-assistant-suggestion__title">
        <SuggestionPrimitive.Title />
      </span>
      <span className="t47-assistant-suggestion__description">
        <SuggestionPrimitive.Description />
      </span>
    </SuggestionPrimitive.Trigger>
  );
}

function ThreadMessage() {
  const role = useAuiState((state) => state.message.role);
  if (role === "user") return <UserMessage />;
  return <AssistantMessage />;
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className="t47-assistant-message t47-assistant-message--user">
      <MessagePrimitive.Content components={{ Text: UserText }} />
    </MessagePrimitive.Root>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="t47-assistant-message t47-assistant-message--assistant">
      <div className="t47-assistant-message__content">
        <MessagePrimitive.Content components={{ Text: MarkdownText }} />
        <MessagePrimitive.Error className="t47-assistant-message__error" />
      </div>
      <AssistantActionBar />
    </MessagePrimitive.Root>
  );
}

function UserText({ text }) {
  return <p>{text}</p>;
}

function MarkdownText() {
  return <MarkdownTextPrimitive className="t47-assistant-markdown" remarkPlugins={[remarkGfm]} />;
}

function AssistantActionBar() {
  return (
    <ActionBarPrimitive.Root hideWhenRunning autohide="not-last" className="t47-assistant-actionbar">
      <ActionBarPrimitive.Copy className="t47-assistant-icon-button" title="Copy response" type="button">
        <CopyIcon aria-hidden="true" size={14} />
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload className="t47-assistant-icon-button" title="Regenerate response" type="button">
        <RefreshCwIcon aria-hidden="true" size={14} />
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
}

function Composer() {
  return (
    <ComposerPrimitive.Root className="t47-assistant-composer">
      <ComposerPrimitive.Input
        aria-label="Question"
        className="t47-assistant-composer__input"
        placeholder="Ask about this record..."
        rows={2}
        submitMode="enter"
      />
      <div className="t47-assistant-composer__actions">
        <AuiIf condition={(state) => !state.thread.isRunning}>
          <ComposerPrimitive.Send className="t47-assistant-send" title="Send message" type="button">
            <ArrowUpIcon aria-hidden="true" size={16} />
          </ComposerPrimitive.Send>
        </AuiIf>
        <AuiIf condition={(state) => state.thread.isRunning}>
          <ComposerPrimitive.Cancel className="t47-assistant-send" title="Stop generating" type="button">
            <SquareIcon aria-hidden="true" size={12} />
          </ComposerPrimitive.Cancel>
        </AuiIf>
      </div>
    </ComposerPrimitive.Root>
  );
}

function ThreadScrollToBottom() {
  return (
    <ThreadPrimitive.ScrollToBottom className="t47-assistant-scroll-button" title="Scroll to bottom" type="button">
      <ArrowDownIcon aria-hidden="true" size={16} />
    </ThreadPrimitive.ScrollToBottom>
  );
}

function textFromContent(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((part) => {
      if (typeof part === "string") return part;
      if (typeof part?.text === "string") return part.text;
      return "";
    })
    .filter(Boolean)
    .join("\n");
}

function buildShareRequest({ source, recordId, messages }) {
  const recordTitle = document.querySelector(".t47-document__header h1")?.textContent.trim() || document.title || "Record";
  const serializedMessages = messages
    .map((message, index) => {
      const content = textFromContent(message.content).trim();
      if (!content) return null;
      return {
        id: message.id || `message-${index + 1}`,
        role: message.role,
        content,
        createdAt: normalizeDate(message.createdAt),
      };
    })
    .filter(Boolean);
  return {
    title: `Transparency47: ${recordTitle}`,
    source,
    recordId,
    recordTitle,
    pageUrl: window.location.href,
    recordMetadata: readRecordMetadata(),
    messages: serializedMessages,
  };
}

function readRecordMetadata() {
  const metadata = {};
  document.querySelectorAll(".t47-metadata-list div").forEach((row) => {
    const key = row.querySelector("dt")?.textContent.trim();
    const value = row.querySelector("dd")?.textContent.trim();
    if (key && value) {
      metadata[key] = value;
    }
  });
  return metadata;
}

function normalizeDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function mount() {
  const rootNode = document.querySelector("[data-record-assistant-root]");
  if (!rootNode) return;
  const params = new URLSearchParams(window.location.search);
  const viewerRoot = document.querySelector(".t47-viewer");
  const source = params.get("source") || viewerRoot?.dataset.defaultSource || "";
  const recordId = params.get("id") || "";
  createRoot(rootNode).render(<RecordAssistant source={source} recordId={recordId} />);
}

mount();
